<template>
  <!-- eslint-disable-next-line vue/no-v-html -->
  <p class="tw:text-left tw:w-full" v-html="$t(subtitleKey, { ConnectivityProvider: providerName })"></p>

  <div
    class="tw:w-full tw:rounded-lg tw:text-left tw:border-2"
    :class="
      variant === 'stepper'
        ? 'tw:bg-orange-200 tw:border tw:border-orange-200'
        : 'tw:bg-red-200 tw:border tw:border-red-200'
    ">
    <div class="tw:p-6 tw:text-sm">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p v-html="$t(stepsKey, { ConnectivityProvider: providerName })"></p>
    </div>

    <template v-if="partnerId != null">
      <div class="tw:bg-white tw:p-4 tw:space-y-3 tw:text-xs tw:rounded-b-lg">
        <div class="tw:flex tw:items-start tw:gap-3">
          <img
            src="@/assets/img/rate-connect/note-icon.svg"
            width="20"
            height="20"
            class="tw:shrink-0"
            alt=""
            aria-hidden="true" />
          <span>{{ $t('rc_cp_fee_info', { ConnectivityProvider: providerName }) }}</span>
        </div>

        <div v-if="extras?.email" class="tw:flex tw:items-center tw:gap-3">
          <img
            src="@/assets/img/rate-connect/email-icon.svg"
            width="20"
            height="20"
            class="tw:shrink-0"
            alt=""
            aria-hidden="true" />
          <span>
            <i18n-t keypath="rc_cp_email" tag="span" scope="global">
              <template #EmailAddress>
                <a :href="`mailto:${extras.email}`" class="tw:text-blue-700 tw:underline">{{ extras.email }}</a>
              </template>
            </i18n-t>
          </span>
        </div>

        <div v-if="extras?.websiteUrl" class="tw:flex tw:items-center tw:gap-3">
          <img
            src="@/assets/img/rate-connect/book-icon.svg"
            width="20"
            height="20"
            class="tw:shrink-0"
            alt=""
            aria-hidden="true" />
          <a :href="extras.websiteUrl" target="_blank" rel="noopener" class="tw:text-blue-700 tw:underline">{{
            $t('rc_cp_website_link', { ConnectivityProvider: providerName })
          }}</a>
        </div>
      </div>
    </template>
  </div>

  <template v-if="providerName && contactUsUrl">
    <p class="tw:text-left tw:w-full">
      {{ $t('rc_rate_check_info', { ConnectivityProvider: providerName }) }}
    </p>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <p class="tw:text-left tw:w-full tw:[&_a]:text-blue-700 tw:[&_a]:underline" v-html="$t('rc_rate_check_help')"></p>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  RC_PROVIDER_EXTRAS,
  RC_RATE_CHECK_DEFAULT_STEPS_KEY,
  getRateCheckStepsKeyCandidate,
} from '@/constants/rcProviderContent'

const props = withDefaults(
  defineProps<{
    // Numeric connectivity-provider partner ID. When set, provider-specific
    // steps/extras are looked up for this partner; when null/undefined
    // (e.g. the "unknown/technical issue" state), only the fallback steps
    // are shown and no extras are rendered.
    partnerId?: number | null
    // Translation key used when there's no partner-specific steps key for
    // this partnerId (or when partnerId isn't set at all).
    fallbackKey?: string
    variant?: 'stepper' | 'modal'
    subtitleKey?: string
    providerName?: string
    contactUsUrl?: string
  }>(),
  {
    partnerId: null,
    fallbackKey: RC_RATE_CHECK_DEFAULT_STEPS_KEY,
    subtitleKey: 'rc_rate_check_cp_subtext',
  }
)

const { te } = useI18n()

// SOLID key resolution: the existence of the `rc_rate_check_<partnerId>`
// translation key is the single source of truth for whether a provider has
// custom steps copy. No separate list of "supported" partner IDs is
// maintained — adding a new provider's copy only requires adding the key to
// the locale files.
const stepsKey = computed(() => {
  if (props.partnerId == null) return props.fallbackKey
  const candidate = getRateCheckStepsKeyCandidate(props.partnerId)
  return te(candidate) ? candidate : props.fallbackKey
})

const extras = computed(() => (props.partnerId != null ? RC_PROVIDER_EXTRAS[props.partnerId] : undefined))
</script>
